import { inr } from "@/lib/api";
import { ArrowLeft } from "lucide-react";

export default function BookingForm({ view, form, setField, rooms, managers = [], nights, balance, emailOk, busy, onBack, onSaveDraft, onReview, onEdit, onConfirm }) {
  const set = setField;
  return (
    <div className="card" data-testid="booking-form">
      <button onClick={onBack} className="flex items-center gap-1 text-sm mb-4" style={{ color: "var(--muted)" }} data-testid="booking-back"><ArrowLeft size={16} /> Back to bookings</button>
      {view === "new" && (
        <>
          <h3 className="font-serif text-3xl mb-6">New Direct Booking</h3>
          <div className="eyebrow mb-3">Guest</div>
          <div className="grid md:grid-cols-3 gap-3 mb-6">
            <input data-testid="bk-name" placeholder="Full name *" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
            <input data-testid="bk-mobile" placeholder="Mobile number *" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} />
            <div>
              <input data-testid="bk-email" type="email" placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)} />
              {!emailOk && <div className="text-xs mt-1" style={{ color: "var(--terracotta)" }} data-testid="bk-email-error">Enter a valid email (must contain @)</div>}
            </div>
          </div>
          <div className="eyebrow mb-3">Stay</div>
          <div className="grid md:grid-cols-4 gap-3 mb-3">
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Check-in *</label><input data-testid="bk-checkin" type="date" value={form.check_in_date} onChange={(e) => set("check_in_date", e.target.value)} /></div>
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Check-out *</label><input data-testid="bk-checkout" type="date" value={form.check_out_date} onChange={(e) => set("check_out_date", e.target.value)} /></div>
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Adults *</label><input data-testid="bk-adults" type="number" min="1" value={form.adults} onChange={(e) => set("adults", e.target.value)} /></div>
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Children</label><input data-testid="bk-children" type="number" min="0" value={form.children} onChange={(e) => set("children", e.target.value)} /></div>
          </div>
          <div className="flex flex-wrap gap-2 mb-6">
            {rooms.map((r) => (
              <button key={r.id} type="button" data-testid={`bk-room-${r.id}`}
                onClick={() => set("room_ids", form.room_ids.includes(r.id) ? form.room_ids.filter((x) => x !== r.id) : [...form.room_ids, r.id])}
                className="text-sm px-4 py-2 rounded-full"
                style={{ background: form.room_ids.includes(r.id) ? "var(--pine)" : "var(--card)", color: form.room_ids.includes(r.id) ? "#fff" : "var(--fg)", border: "1px solid var(--border)" }}>
                {r.name}
              </button>
            ))}
          </div>
          <div className="eyebrow mb-3">Responsible Manager</div>
          <div className="grid md:grid-cols-2 gap-3 mb-6">
            <select data-testid="bk-manager" value={form.responsible_manager_id} onChange={(e) => set("responsible_manager_id", e.target.value)}>
              <option value="">Unassigned</option>
              {managers.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.email}</option>)}
            </select>
            <p className="text-xs self-center" style={{ color: "var(--muted)" }}>Food orders &amp; service requests for this stay auto-assign to this manager after check-in.</p>
          </div>
          <div className="eyebrow mb-3">Payment</div>
          <div className="grid md:grid-cols-4 gap-3 mb-4">
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Total booking amount *</label><input data-testid="bk-amount" type="number" min="0" value={form.booking_amount} onChange={(e) => set("booking_amount", e.target.value)} /></div>
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Advance received *</label><input data-testid="bk-advance" type="number" min="0" value={form.advance_amount} onChange={(e) => set("advance_amount", e.target.value)} /></div>
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Method</label>
              <select data-testid="bk-method" value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)}>
                <option>UPI</option><option>Bank Transfer</option><option>Cash</option><option>Card</option>
              </select></div>
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Reference / UTR</label><input data-testid="bk-ref" value={form.payment_reference} onChange={(e) => set("payment_reference", e.target.value)} /></div>
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Payment date</label><input data-testid="bk-paydate" type="date" value={form.payment_date} onChange={(e) => set("payment_date", e.target.value)} /></div>
            <div className="md:col-span-3"><label className="text-xs" style={{ color: "var(--muted)" }}>Internal notes</label><input data-testid="bk-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
          <div className="card mb-6" style={{ background: "var(--surface)" }}>
            <div className="flex flex-wrap gap-8">
              <div><div className="text-xs" style={{ color: "var(--muted)" }}>Number of nights</div><div className="font-serif text-2xl" data-testid="bk-nights">{nights}</div></div>
              <div><div className="text-xs" style={{ color: "var(--muted)" }}>Total booking</div><div className="font-serif text-2xl">{inr(parseFloat(form.booking_amount) || 0)}</div></div>
              <div><div className="text-xs" style={{ color: "var(--muted)" }}>Advance received</div><div className="font-serif text-2xl">-{inr(parseFloat(form.advance_amount) || 0)}</div></div>
              <div><div className="text-xs" style={{ color: "var(--terracotta)" }}>Balance remaining</div><div className="font-serif text-2xl" style={{ color: "var(--terracotta)" }} data-testid="bk-balance">{inr(balance)}</div></div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button data-testid="bk-save-draft" onClick={onSaveDraft} disabled={busy} className="btn-outline">Save Draft</button>
            <button data-testid="bk-review" onClick={onReview} className="btn-primary">Review Booking</button>
          </div>
        </>
      )}
      {view === "review" && (
        <>
          <h3 className="font-serif text-3xl mb-6">Review Booking</h3>
          <div className="max-w-lg space-y-2 mb-6" data-testid="bk-review-summary">
            <div className="eyebrow mb-2">Freebird Nest</div>
            <Row2 k="Guest" v={form.full_name} />
            <Row2 k="Mobile" v={form.mobile} />
            <Row2 k="Email" v={form.email || "—"} />
            <Row2 k="Check-in" v={form.check_in_date} />
            <Row2 k="Check-out" v={form.check_out_date} />
            <Row2 k="Guests" v={`${form.adults} adults${parseInt(form.children) > 0 ? `, ${form.children} children` : ""}`} />
            <Row2 k="Rooms" v={rooms.filter((r) => form.room_ids.includes(r.id)).map((r) => r.name).join(", ")} />
            <Row2 k="Responsible Manager" v={managers.find((m) => m.id === form.responsible_manager_id)?.name || "Unassigned"} />
            <Row2 k="Total booking" v={inr(parseFloat(form.booking_amount) || 0)} />
            <Row2 k="Advance received" v={inr(parseFloat(form.advance_amount) || 0)} />
            <div className="hairline my-2" />
            <Row2 k="Balance remaining" v={inr(balance)} accent />
          </div>
          <div className="flex flex-wrap gap-3">
            <button data-testid="bk-edit" onClick={onEdit} className="btn-outline">Edit Booking</button>
            <button data-testid="bk-confirm" onClick={onConfirm} disabled={busy} className="btn-primary">{busy ? "Confirming…" : "Confirm & Send"}</button>
          </div>
        </>
      )}
    </div>
  );
}

function Row2({ k, v, accent }) {
  return (
    <div className="flex justify-between">
      <span className="text-sm" style={{ color: "var(--muted)" }}>{k}</span>
      <span className="font-medium" style={{ color: accent ? "var(--terracotta)" : "var(--fg)" }}>{v}</span>
    </div>
  );
}
